import { getKeys, Prim } from "@base/util/TypeUtil";
import { deepEqual, getObjectDiff, Util } from "@base/util/Util";
import { AdditionalArticleInfos, AdditionalArticleInfosOption, IArticle, IArticleColorSmall, IArticleExtraPrice, IRuleArticle, RainCutVariant, ShoppingArticleType } from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureStore } from "@biz/definitions/ArticleFeatureDefinitions";
import { PowderCoating } from "@biz/definitions/PowderCoatingDefinitions";
import { IPriceOptions, PriceListIndex } from "@biz/definitions/PriceDefinitions";
import { PoleId } from "@biz/definitions/SunSailDefinitions";
import { RaffFieldCalculationType } from "@biz/items/raff/RaffDefinitions";
import { getQuickOverlayPriority, IBeosOrderProperties, IShoppingArticleOptions, IShoppingListContext, IShoppingListItem } from "@biz/models/ShoppingListDefinitions";
import { getTieBackVariant } from "@biz/orders/BeosVarianten";

export class RuleArticleOptions {
  static getArticleType(options: Partial<IShoppingArticleOptions>): ShoppingArticleType {
    return options.articleType ?? "obligatory";
  }

  static getCount(options: Partial<IShoppingArticleOptions>): number {
    if (options?.count !== undefined)
      return options.count;
    return RuleArticleOptions.getArticleType(options) === "optional" ? 0 : 1;
  }

  static getGranularity(options: Partial<IShoppingArticleOptions>): number {
    return options?.granularity ?? 1;
  }

}
export class RuleArticleLogic {
  static removeEmpty<T extends {}>(a: T | undefined): T | undefined {
    if (!a)
      return undefined;

    const ret: any = {};
    for (const k of getKeys(a)) {
      const v = a[k]
      if (v !== undefined && v !== null)
        ret[k] = v;
    }
    if (Object.keys(ret).length === 0)
      return undefined;
    return ret;
  }

  static optimizeRuleArticle(ra: IRuleArticle): IRuleArticle {
    if (ra.beosOrder) {
      const optimizedBeosOrder: IBeosOrderProperties = {
        ...ra.beosOrder,
        varianten: RuleArticleLogic.removeEmpty(ra.beosOrder.varianten)
      }
      ra.beosOrder = RuleArticleLogic.removeEmpty(optimizedBeosOrder);
      if (!ra.beosOrder)
        delete ra.beosOrder
    }
    if (ra.additionalArticleInfo) {
      ra.additionalArticleInfo = RuleArticleLogic.removeEmpty(ra.additionalArticleInfo);
      if (!ra.additionalArticleInfo)
        delete ra.additionalArticleInfo

    }
    return ra;
  }

  static createRuleArticleFromArticle(id: string, articleInfo: IArticle, companySurcharge: number, options: Partial<IShoppingArticleOptions>): IRuleArticle {
    const ra = RuleArticleLogic.createRuleArticle(id, articleInfo.articleId, options);
    ra.articlePrice = RuleArticleLogic.calcRuleArticlePrice(ra, articleInfo, options.priceListIndex || "PL5", companySurcharge)
    return ra;
  }

  static createRuleArticle(id: string, articleNumber: string, options: Partial<IShoppingArticleOptions>): IRuleArticle {
    const granularity = RuleArticleOptions.getGranularity(options);
    const articleType = RuleArticleOptions.getArticleType(options);
    const count = RuleArticleOptions.getCount(options);

    const ssa: IRuleArticle = {
      id,
      articleNumber,
      articleType,
      count,
      articlePrice: 99999, // to be overriden
      countClientAvailable: 0,
      partList: options.partList,
      quickOverlayPriority: options.quickOverlayPriority,
      shoppingCartPriority: options.shoppingCartPriority,
      beosOrder: options.beosOrder,
      priceListIndex: options?.priceListIndex || "PL0",
      offerList: options.offerList,
      computed: options.computed,
      mul: options?.mul === undefined ? undefined : Util.round(options.mul, 3),
      affiliation: {},
      articleGroup: options?.articleGroup,
      customArticle: options?.customArticle,
      maxUnits: granularity * (options?.maxUnits ?? 0),
      stkUnit: options?.stkUnit,
      mulUnit: options?.mulUnit,
      idMode: options.idMode,
      addToCart: options.addToCart,
      features: options?.features,
      featureTemplate: options?.featureTemplate,
      granularity
    }

    ssa.affiliation = options.affiliation || {};
    if (ssa.affiliation?.poleId === -1 as PoleId)
      ssa.affiliation.poleId = undefined;

    if (options.quickCategory) {
      ssa.affiliation.quickCategory = options.quickCategory;
    }

    return ssa;
  }

  static additionalArticleInfosCanBeMerged(a: AdditionalArticleInfos | undefined, b: AdditionalArticleInfos | undefined): boolean {
    // if (a?.length !== b?.length) // length for poles... doesn't work since it's not the length of the array, but e.g. the length property of the poles...
    //   return false;
    return deepEqual(a, b);
  }

  static mergeRuleArticle(ssa: IRuleArticle, alreadyExisting: IRuleArticle | undefined): IRuleArticle {
    if (!alreadyExisting)
      return ssa;

    const unify = (a: IRuleArticle): Partial<IRuleArticle> => {
      return { ...a, count: 1, additionalArticleInfo: undefined, beosOrder: undefined, featureArticles: undefined };
    }
    if (!deepEqual(unify(alreadyExisting), unify(ssa))) {
      console.warn("Article differs and can't be merged! ", ssa.id)
      console.log(getObjectDiff(unify(alreadyExisting), unify(ssa)))
      console.log(unify(alreadyExisting))
      console.log(unify(ssa))

      throw new Error("Article is already included: " + ssa.id)
    }
    if (!RuleArticleLogic.additionalArticleInfosCanBeMerged(alreadyExisting.additionalArticleInfo, ssa.additionalArticleInfo)) {
      throw new Error("Article is already included (additionalArticleInfo): " + ssa.id)
    }
    const merged = {
      ...ssa,
      count: ssa.count + alreadyExisting.count,
      countClientAvailable: (ssa.countClientAvailable || 0) + (alreadyExisting.countClientAvailable || 0),
    }
    return merged;
  }

  static assignAdditionalOptions(ssa: IRuleArticle, additionalArticleInfo: AdditionalArticleInfosOption | undefined, slc: IShoppingListContext): void {
    if (!additionalArticleInfo)
      return;
    for (const k of Object.keys(additionalArticleInfo)) {
      const aai = additionalArticleInfo[k];
      if (typeof aai === "function") {
        const value = aai(ssa, { lc: slc.lc });
        if (value) {
          ssa.additionalArticleInfo = { ...ssa.additionalArticleInfo, [k]: value }
        }
      } else if (aai) {
        ssa.additionalArticleInfo = { ...ssa.additionalArticleInfo, [k]: aai}
      }
    }
  }

  static assignQuickOverlayPriority(ssa: IRuleArticle, priorityId: string): void {
    if (ssa.quickOverlayPriority === undefined)
      ssa.quickOverlayPriority = getQuickOverlayPriority(priorityId);
  }

  static assignWishListItem(ruleArticle: IRuleArticle, sli: IShoppingListItem | undefined): void {
    if (!sli)
      return;

    if (ruleArticle.articleType !== "obligatory") {
      if (sli.count !== undefined) {
        ruleArticle.count = sli.count
      }
      if (sli.mul !== undefined) {
        ruleArticle.mul = sli.mul;
      }
      if (sli.addedSlotTime !== undefined) {
        ruleArticle.addedSlotTime = sli.addedSlotTime;
      }
    }
    ruleArticle.countClientAvailable = sli.countClientAvailable || 0;
  }

  static handlePowderCoating(slc: IShoppingListContext, ruleArticle: IRuleArticle, sli: IShoppingListItem | undefined): void {

    if (slc.articleMap[ruleArticle.articleNumber]?.powderCoating) {
      // const mergedPowderCoating = PowderCoating.merge(sli.powderCoating, slc.powderCoating);
      ruleArticle.powderCoating = PowderCoating.getPowderCoating(sli?.powderCoatingIndex, slc.powderCoating)

      if (ruleArticle.powderCoating) {
        if (!ruleArticle.additionalArticleInfo)
          ruleArticle.additionalArticleInfo = {};
        const text = ruleArticle.powderCoating + " - " + PowderCoating.getPowderColorMap()[ruleArticle.powderCoating]?.name;
        ruleArticle.additionalArticleInfo.powderCoating = { header: slc.lc.t("ArticleInfo_PowderCoating"), text, flag: "important" }
      }
    }
  }

  /** @deprecated */
  static calcIdFromKeyObjectOld(keyObject: any): string {
    const keys = Object.keys(keyObject)
    // normalize idKeyObject
    let count = 0;
    for (const k of keys) {
      const val = keyObject[k];
      if (val === undefined || val === "")
        delete keyObject[k];
      else
        count++;
    }

    if (count === 0)
      return "";

    const id = JSON.stringify(keyObject, keys.sort());
    return id;
  }

  static #calcBasicArticlePrice(article: IArticle, companySurcharge: number, priceListIndex: PriceListIndex): number {
    // console.log("PriceListIndex :", `${priceListIndex} ${article._id}`)
    if (!article?.priceValues)
      return 0;
    const price = article.priceValues[priceListIndex === "PL5" ? 1 : 0] * (1.0 + companySurcharge / 100.0);
    return price;
  }

  static calcRuleArticlePrice(ruleArticle: IRuleArticle, articleInfo: IArticle, priceListIndex: PriceListIndex, companySurcharge: number): number {
    return RuleArticleLogic.calcPricePerItem(articleInfo, companySurcharge, RuleArticleLogic.calcPriceOptions(ruleArticle.mul, priceListIndex, ruleArticle.features))
  }

  static calcPriceOptions(mul: number | undefined, priceListIndex: PriceListIndex, features: ArticleFeatureStore | undefined): IPriceOptions {
    return {
      mul: mul,
      priceListIndex,
      colorId: features?.colorId ? "" + features.colorId : undefined,
      rainCut: features?.rainCut,
      fieldCalculationType: features?.raffFieldCalculationType,
      tieBackLength: features?.tieBackLength,
      standardSize: features?.standardSize,
    }
  }

  static calcPricePerItem(article: IArticle, companySurcharge: number, options?: IPriceOptions): number {
    // if (article._id === "60950B") {
    //   console.log("BLA!");
    // }
    let price = RuleArticleLogic.#calcBasicArticlePrice(article, companySurcharge, options?.priceListIndex || "PL0");

    if (article?.scalePrices) {
      const quantity = options?.mul ?? 1;
      const roundedQuantity = Util.round(quantity, 2);
      for (let i = article.scalePrices.length - 1; i >= 0; i--) {
        const sp = article.scalePrices[i];
        if (roundedQuantity >= sp.quantity) {
          price = sp.priceValues[0] * (1.0 + companySurcharge / 100.0);
          break;
        }
      }
    }

    let colorFactorP = 1.0;
    let colorExtraCharge = 0;
    const colorItem = RuleArticleLogic.getColorItem(article, options?.colorId);
    if (colorItem) {
      colorExtraCharge = colorItem.extraCharge;
      if (colorItem.factorP !== undefined) colorFactorP = colorItem.factorP;
    }

    let standardSizeFactor = 1.0;
    if (article?.sizeGroup && options?.standardSize) {
      const ssi = article.sizeGroup.items.find((i) => i.variantId === options.standardSize);
      if (ssi) standardSizeFactor = ssi.factor;
    }

    let tieBackExtraCharge = 0;
    if (article?.tieBackLengthGroup && options?.tieBackLength) {
      if (getTieBackVariant(options.tieBackLength) === "LANG") {
        const found = article.tieBackLengthGroup.items.find((item) => item.variantId === "LANG")
        if (found)
          tieBackExtraCharge = found.extraCharge;
      }
    }

    const rainCutExtraCharge = (options?.rainCut && RuleArticleLogic.getRainCutExtraPrice(options.rainCut, article, colorItem)?.extraCharge) || 0;
    const raffFactor = options?.fieldCalculationType === RaffFieldCalculationType.IndividualFields ? 1.35 : 1.0;

    if (!price) // blackened server
      return 0;

    const totalPrice = (price * standardSizeFactor * colorFactorP + colorExtraCharge + rainCutExtraCharge) * raffFactor + tieBackExtraCharge;
    return totalPrice;
  }

  static getColorItem(article: IArticle, colorId: string | undefined): IArticleColorSmall | undefined {
    if (article?.colorGroup && colorId) {
      return article.colorGroup.items.find((i) => i.variantId === colorId);
    }
    return undefined
  }

  static getRainCutVariantId(rainCut: boolean | undefined, article: IArticle, colorItem: IArticleColorSmall | undefined): RainCutVariant | undefined {
    const isSpecialColor = colorItem && (colorItem.specialColor || colorItem.style);

    let ret: ("RAINCUT" | "RAINCU2" | "STANDAR" | "STANDA2")[] = [];
    if (rainCut) {
      ret = isSpecialColor ? ["RAINCU2", "RAINCUT"] : ["RAINCUT"];
    } else {
      ret = isSpecialColor ? ["STANDA2", "STANDAR"] : ["STANDAR"];
    }

    for (const r of ret) {
      if (article.rainCutGroup && article.rainCutGroup.items.find((i) => i.variantId === r))
        return r;
    }
    return undefined;
  }

  static getRainCutExtraPrice(rainCut: boolean | undefined, article: IArticle, colorItem: IArticleColorSmall | undefined): IArticleExtraPrice | undefined {
    const variantId = RuleArticleLogic.getRainCutVariantId(rainCut, article, colorItem);
    if (variantId) {
      const ret = article.rainCutGroup && article.rainCutGroup.items.find((i) => i.variantId === variantId);
      return ret;
    }
    return undefined;
  }

  static calcPricePerItemForId(slc: IShoppingListContext, articleId: string, options: IPriceOptions): number | undefined {
    const article = slc.articleMap[articleId];
    if (!article && articleId) {
      slc.missingArticles.push(articleId);
      return undefined;
    }
    return RuleArticleLogic.calcPricePerItem(article, slc.company.surcharge, options);
  }

  static calcIdFromKeyObjectNew(obj: Record<string, Prim>): string {
    const ret: string[] = [];
    const keys = Object.keys(obj).sort();
    for (const k of keys) {
      const val = obj[k];
      if (val !== undefined) {
        const valStr = val?.toString();
        if (valStr)
          ret.push(k + "=" + val?.toString());
      }
    }
    return ret.join("|");
  }
}